<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import { storeToRefs } from "pinia";
import QrcodeVue from "qrcode.vue";
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from "vue-router";
import { vMuted } from "#/directives/vMuted";
import { useScreenBackground } from "#/hooks/useScreenBackground/useScreenBackground";
import { useCardStore } from "#/store/card";
import { useCartStore } from "#/store/cart";
import { useDecorationStore } from "#/store/decoration";
import { useMainStore } from "#/store/main";
import { useNutritionBasisStore } from "#/store/nutritionBasis";
import { useThemeStore } from "#/store/theme";
import { useUserStore } from "#/store/user";
import clearLocalStorage from "#/utils/clearLocalStorage";

const storeMain = useMainStore();
const storeCart = useCartStore();
const storeCard = useCardStore();
const nutritionBasis = useNutritionBasisStore();
const themeStore = useThemeStore();

const { cartProducts_lib } = storeToRefs(storeMain);
const { orderNumber, orderExternalId, orderCompleted_lib, selectedOrderType, markedItemsSnapshot } = storeToRefs(storeCart);
const { is_startPage } = storeToRefs(storeCard);
const props = defineProps<{ isKiosk?: boolean; decoration?: string }>();

const { mediaUrl, videoMimeType, isImage } = useScreenBackground(() => !!props.isKiosk);

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const { t } = useTranslation();
const decor = useDecorationStore().screen(props.decoration);

const TIMEOUT_DELAY = 15_000;
const COUNTDOWN_STEP = 1_000;

let timeoutStatus: any = null;
let countdownStatus: ReturnType<typeof setInterval> | null = null;
const isRedirectRunning = ref(false);
const secondsLeft = ref(TIMEOUT_DELAY / COUNTDOWN_STEP);

const paidVariant = computed(() => decor.variant("paid", "") || undefined);
const logoSource = computed(() => decor.icon("paid_logo")?.url || themeStore.data?.logo || null);
const isCountdown = computed(() => decor.variant("paid_bar", "") === "countdown");
const barStyle = computed(() =>
	isCountdown.value ? { "--paid-countdown": `${TIMEOUT_DELAY}ms`, animationPlayState: isRedirectRunning.value ? "running" : "paused" } : undefined,
);

const isMarkedNoticeBlocking = computed(() =>
  !!themeStore.data?.kiosk_ask_staff_to_scan_mark_text && markedItemsSnapshot.value.length > 0
);

const qrLink = computed(() => {
	if (storeMain.config_lib.queue_domain && orderExternalId.value) return `${storeMain.config_lib.queue_domain}/order/${orderExternalId.value}`;
	return null;
});
const imageSettings = computed(() => ({
	src: themeStore.data?.logo || "",
	width: 60,
	height: 60,
	excavate: true,
}));

const resetSession = () => {
	clearLocalStorage(["cart"]);
	storeCart.isAdultConfirmed = false;
	cartProducts_lib.value = [];
	orderCompleted_lib.value = null;
	selectedOrderType.value = null;
	orderNumber.value = null;
	orderExternalId.value = null;
	storeMain.isOrderTypeSubmited = false;
	storeCart.clearCard();
	nutritionBasis.isPer100g = true;
	userStore.logout();
	router.replace(`/${route.params.orgId}`);
};

const startRedirectTimer = () => {
  if (timeoutStatus) return;
	timeoutStatus = setTimeout(() => {
		is_startPage.value = !themeStore.data?.kiosk_disable_lock_screen;
		resetSession();
	}, TIMEOUT_DELAY);
	isRedirectRunning.value = true;
	if (!isCountdown.value) return;
	secondsLeft.value = TIMEOUT_DELAY / COUNTDOWN_STEP;
	countdownStatus = setInterval(() => {
		secondsLeft.value = Math.max(0, secondsLeft.value - 1);
	}, COUNTDOWN_STEP);
};

const cancelRedirectTimer = () => {
  if (timeoutStatus) {
    clearTimeout(timeoutStatus);
    timeoutStatus = null;
  }
  if (countdownStatus) {
    clearInterval(countdownStatus);
    countdownStatus = null;
  }
  isRedirectRunning.value = false;
};

onMounted(() => {
  if (!isMarkedNoticeBlocking.value) startRedirectTimer();
})

watch(isMarkedNoticeBlocking, (blocking) => {
  if (blocking) cancelRedirectTimer();
  else startRedirectTimer();
});

const handleClick = () => {
	resetSession();
};

onUnmounted(() => {
	cancelRedirectTimer();
});
</script>

<template>
	<div class="start_page" :data-paid="paidVariant" v-bind="decor.bind('paid')">
		<template v-if="decor.visible('paid_media')">
			<img v-if="mediaUrl && isImage" :key="mediaUrl" :src="mediaUrl" alt="" crossorigin="anonymous">
			<video v-else-if="mediaUrl" loop autoplay muted playsinline v-muted crossorigin="anonymous" :key="mediaUrl">
				<source :src="mediaUrl" :type="videoMimeType">
			</video>
		</template>
		<div v-if="decor.visible('paid_shade')" class="start_img_black" :class="{ darken: themeStore.data?.kiosk_preloader_darken }"></div>
		<div class="wrap" v-bind="decor.bind('paid_wrap')">
			<div class="wrap_box" v-bind="decor.bind('paid_card')">
				<img v-if="decor.visible('paid_logo', false) && logoSource" class="paid_logo" :src="logoSource" alt="" v-bind="decor.bind('paid_logo')">
				<div v-if="decor.visible('paid_icon')" class="icon" v-bind="decor.bind('paid_icon')">
					<svg viewBox="0 0 24 24" aria-hidden="true">
						<path d="M20 6L9 17l-5-5" />
					</svg>
				</div>
				<h1 v-bind="decor.bind('paid_title')">{{ themeStore.data?.kiosk_success_screen_title || t(`cart-complete.kiosk-success.title`) }}</h1>
				<div class="paid_plate" v-bind="decor.bind('paid_plate')">
					<span v-if="decor.visible('paid_plate_label', false)" class="paid_plate_label" v-bind="decor.bind('paid_plate_label')">
						{{ t(`cart-complete.kiosk-success.order-label`) }}
					</span>
					<p class="title_fiscal_order" v-bind="decor.bind('paid_number')">№ {{ orderNumber }}</p>
				</div>
				<p class="title_fiscal_completed" v-bind="decor.bind('paid_subtitle')">
					{{ themeStore.data?.kiosk_success_screen_subtitle || t(`cart-complete.kiosk-success.subtitle`) }}
				</p>
				<p v-if="qrLink && decor.visible('paid_hint', false)" class="paid_hint" v-bind="decor.bind('paid_hint')">
					{{ t(`cart-complete.kiosk-success.track-hint`) }}
				</p>
				<div v-if="qrLink" class="title_fiscal_qr_wrapper" v-bind="decor.bind('paid_qr_wrap')">
					<div  class="qr_border" v-bind="decor.bind('paid_qr')">
						<QrcodeVue :value="qrLink" :size="200" level="Q" render-as="svg" :image-settings="imageSettings" />
					</div>
				</div>
				<div class="wrapper_accept" v-bind="decor.bind('paid_actions')">
					<button class="btn" type="button" v-bind="decor.bind('paid_button')" @click="handleClick">
						<span class="label" v-bind="decor.bind('paid_label')">{{ t(`cart-complete.kiosk-success.button.accept`) }}<span
							v-if="isCountdown && decor.visible('paid_seconds')"
							class="paid_seconds"
							v-bind="decor.bind('paid_seconds')"
						>{{ secondsLeft }}</span></span>
						<span
							ref="barRef"
							class="bar_complete"
							aria-hidden="true"
							:data-bar="isCountdown ? 'countdown' : undefined"
							v-bind="decor.bind('paid_bar')"
							:style="barStyle"
						></span>
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
  @use './cartCompleteKioskSuccess.scss';
</style>
